import { Box, Flex } from '@chakra-ui/react';
import React from 'react';

import { useCodingConfig } from '../../interface';
import { ClinicalDocumentViewer } from '../../molecules/ClinicalDocumentViewer';
import { DocumentTitleNav } from '../../molecules/DocumentTitleNav';
import { TabSelectionProvider } from '../../molecules/TabSelectionContext';
import {
  CARD_BG,
  CARD_BORDER,
  CARD_RADIUS,
  CARD_SHADOW,
  RAIL_WIDTH,
  STRIP_BORDER
} from '../../styles/tokens';

/**
 * LEFT-region organism -- composes the document tab strip above the scrollable document body, in a
 * bounded flex column so `ClinicalDocumentViewer`'s `overflow-y:auto` has a definite height to scroll
 * within (a child's `overflow-y:auto` needs a bounded ancestor to take effect). Pure composition, no
 * data logic of its own beyond seeding the pane's tab selection -- each molecule independently reads
 * its own config slice via `useCodingConfig()`. Does NOT render a `<main>` (CMx already provides one);
 * the `role="region"`/`aria-label="Documentation"` landmark here is this organism's own labelled
 * region.
 *
 * TAB SELECTION: `DocumentationPane` now DOES read config -- the one piece of "data logic" it owns is
 * computing which doc tab is selected BY DEFAULT, so it wraps `DocumentTitleNav` + the viewer row in
 * `<TabSelectionProvider defaultContentKey={firstVisibleTabKey}>`. `firstVisibleTabKey` is
 * `left.tabs`' first visible tab ascending by `order` (`undefined` when `left.tabs` is `[]`, the
 * empty-shell default -- `TabSelectionProvider` then seeds nothing selected, exactly matching the
 * previous shape). The earlier "organisms read no context" note above was a convenience, not a hard
 * constraint: per-pane tab SELECTION genuinely belongs at the pane level -- one `TabSelectionProvider`
 * per pane, so the left and right tab strips never share a selection -- rather than being threaded
 * down as a prop from a still-higher, still-context-free ancestor. `DocumentTitleNav`/
 * `ClinicalDocumentViewer` each independently read the resulting selection via `useTabSelection()`;
 * this organism only seeds the INITIAL value and holds no selection state of its own.
 *
 * CHROME + LEFT RAIL (styles/tokens.ts): the root becomes the rounded/bordered/shadowed white "pane
 * card" `empty-ui.png` shows (`CARD_BG`/`CARD_BORDER`/`CARD_RADIUS`/`CARD_SHADOW`);
 * `overflow="hidden"` clips `DocumentTitleNav`'s square-cornered strip to the card's own rounded
 * corners, the same reason `DocumentTitleNav`/`ReviewTransactionsTabs` already clip their own tab
 * content. The border itself is set via a raw `style` object, not Chakra's `borderWidth`/
 * `borderStyle`/`borderColor` props -- jsdom's `getComputedStyle` does not reliably expose border
 * sub-properties set through a stylesheet/emotion class (unlike `borderRadius`, a plain property,
 * which reads back fine either way), so `DocumentationPane.test.tsx`'s card assertion needs the
 * inline form to see it; the SAME "raw style for the property a test inspects" split `TopBarContainer`/
 * `PaneFooterSummary` already use for height. Below the strip, the BODY is a row: a thin decorative
 * left rail (`RAIL_WIDTH` wide, `STRIP_BORDER` border-right) beside `ClinicalDocumentViewer`.
 *
 * The rail is DELIBERATELY a plain presentational `Box`, not a config-driven container: giving it a
 * `left.docRail` config marker (mirroring the other ten `ContainerContentKey` containers) was
 * considered and rejected -- it would ripple into `schema.ts`/`defaultConfig.ts`/their tests to add a
 * field with no behavior yet, for no benefit this task needs (the `TabSelectionProvider` wrap above
 * needs `left.tabs`, not a rail identity marker). The rail carries a stable literal
 * `data-content-key="docRail"` so tests/future wiring can still find it by the same convention every
 * other container uses, without the config/architecture change. It is `aria-hidden` -- pure chrome,
 * not a named content region like its ten config-backed siblings.
 */
export const DocumentationPane: React.FC = () => {
  const { left } = useCodingConfig();
  const firstVisibleTabKey = left.tabs
    .filter((tab) => tab.visible)
    .sort((a, b) => a.order - b.order)[0]?.contentKey;

  return (
    <Flex
      role="region"
      aria-label="Documentation"
      direction="column"
      h="100%"
      minH={0}
      bg={CARD_BG}
      borderRadius={CARD_RADIUS}
      boxShadow={CARD_SHADOW}
      overflow="hidden"
      style={{
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: CARD_BORDER
      }}
    >
      <TabSelectionProvider defaultContentKey={firstVisibleTabKey}>
        <DocumentTitleNav />
        <Flex flex="1" minH={0}>
          <Box
            data-content-key="docRail"
            aria-hidden="true"
            flexShrink={0}
            w={RAIL_WIDTH}
            h="100%"
            bg={CARD_BG}
            borderRight={`1px solid ${STRIP_BORDER}`}
          />
          <ClinicalDocumentViewer />
        </Flex>
      </TabSelectionProvider>
    </Flex>
  );
};
